import { useCallback } from "react";
import { explainFunctionError, functionErrorResponse, useSupabase, useTenant } from "@how/app-kit";

/**
 * The channel a YouTube handle address names, from the resolve-youtube-channel function (ADR 0033, second amendment
 * of 2026-10-03). Asked once, when staff add the address in the Live now block's form; the block stores the answer.
 * Resolves to the channel id, or rejects with the function's own sentence, which says how to paste the channel's
 * address by hand.
 */
export function useFindYoutubeChannel(): (address: string) => Promise<string> {
  const supabase = useSupabase();
  const tenant = useTenant();
  const tenantId = tenant.status === "resolved" ? tenant.tenant.tenantId : null;
  return useCallback(
    async (address: string) => {
      const result = await supabase.functions.invoke<{ handle: string; channel_id: string }>(
        "resolve-youtube-channel",
        { body: { tenant_id: tenantId ?? "", address } },
      );
      if (result.error) {
        throw await explainFunctionError(result.error, functionErrorResponse(result.error));
      }
      const id = result.data?.channel_id;
      if (typeof id !== "string" || !/^UC[A-Za-z0-9_-]{22}$/.test(id)) {
        throw new Error(
          "YouTube's answer named no channel. Paste the channel's own address instead.",
        );
      }
      return id;
    },
    [supabase, tenantId],
  );
}
